import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

const PREDICTIVE_SEARCH_LEGACY_RESULTS_ID = 'predictive-search-results';
const PREDICTIVE_SEARCH_LEGACY_PRODUCTS_ID = 'predictive-search-products';
const PREDICTIVE_SEARCH_PRODUCT_FIELDS =
  'title,product_type,variants.title,vendor,body,tag,variants.sku,variants.barcode';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @property {HTMLElement} [searchFooterTerms] - Footer line showing the active search terms.
 * @property {HTMLButtonElement} [viewAllButton] - "View all" / submit control in the form footer.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  get #resultsListboxId() {
    return this.dataset.predictiveResultsId || PREDICTIVE_SEARCH_LEGACY_RESULTS_ID;
  }

  get #productsContainerId() {
    return this.dataset.predictiveProductsId || PREDICTIVE_SEARCH_LEGACY_PRODUCTS_ID;
  }

  /**
   * Section Rendering API responses use legacy ids; remap so modal + header instances stay unique in the document.
   * @param {Document | Element | null} root
   */
  #rewritePredictiveIdsInRoot(root) {
    if (!root) return;
    const scope = root instanceof Document ? root.documentElement : root;
    if (!(scope instanceof Element)) return;

    const listId = this.#resultsListboxId;
    const prodId = this.#productsContainerId;

    for (const el of scope.querySelectorAll(`[id="£{PREDICTIVE_SEARCH_LEGACY_RESULTS_ID}"]`)) {
      el.id = listId;
    }
    for (const el of scope.querySelectorAll(`[id="£{PREDICTIVE_SEARCH_LEGACY_PRODUCTS_ID}"]`)) {
      el.id = prodId;
    }
  }

  #handleSearchSubmenuOpen = () => {
    this.#loadEmptyState();
  };

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  get #recentlyViewedInPredictiveSearchEnabled() {
    return this.dataset.recentlyViewedEnabled === 'true';
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleSearchModalOpenFocus, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    const searchMenu = this.closest('search-menu');
    if (searchMenu) {
      searchMenu.addEventListener('theme:search-submenu-open', this.#handleSearchSubmenuOpen, { signal });
    }

    if (this.#recentlyViewedInPredictiveSearchEnabled && RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (
      this.#recentlyViewedInPredictiveSearchEnabled &&
      !this.#emptyStateLoaded &&
      RecentlyViewed.getProducts().length > 0
    ) {
      this.#loadEmptyState();
    }
  };

  #handleSearchModalOpenFocus = () => {
    const dialogComponent = this.dialog;
    if (!dialogComponent?.hasAttribute('data-mobile-drawer-close')) return;

    const focusInput = () => {
      const input = this.refs.searchInput;
      if (!(input instanceof HTMLInputElement)) return;

      try {
        input.focus({ preventScroll: true, focusVisible: false });
      } catch {
        input.focus({ preventScroll: true });
      }
    };

    focusInput();
    queueMicrotask(focusInput);
    requestAnimationFrame(focusInput);
  };

  #loadEmptyState() {
    if (!this.#recentlyViewedInPredictiveSearchEnabled) return;
    if (this.#emptyStateLoaded) return;
    if (RecentlyViewed.getProducts().length === 0) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement && this.#isFocusableForModalTab(item));

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * @param {HTMLElement} el
   */
  #isFocusableForModalTab(el) {
    if (el.hidden) return false;
    if (el instanceof HTMLButtonElement && el.disabled) return false;
    if (el instanceof HTMLInputElement && el.disabled) return false;
    return el.getClientRects().length > 0;
  }

  /** @returns {HTMLButtonElement | null} */
  #getModalDrawerCloseButton() {
    const root = this.dialog;
    if (!root) return null;
    const btn = root.querySelector('.search-modal__drawer-close');
    return btn instanceof HTMLButtonElement ? btn : null;
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');
        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        activeItem = item;
      } else {
        item.removeAttribute('aria-selected');
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  #clearResultsListSelection() {
    this.#isKeyboardNavigation = false;
    for (const item of this.#allResultsItems) {
      item.removeAttribute('aria-selected');
      item.classList.remove('keyboard-focus');
    }
  }

  /** @param {unknown} el */
  #isViewAllButtonFocusable(el) {
    if (!(el instanceof HTMLButtonElement)) return false;
    if (el.disabled) return false;
    if (el.hidden) return false;
    return el.getClientRects().length > 0;
  }

  /** @param {Event} [event] */
  submitViewAllSearch = (event) => {
    event?.preventDefault();
    const form = this.refs.form;
    if (form instanceof HTMLFormElement) form.requestSubmit();
  };

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (event.key === 'Enter' && document.activeElement === this.refs.viewAllButton) {
      event.preventDefault();
      this.submitViewAllSearch();
      return;
    }

    if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const totalItems = this.#allResultsItems.length;

    if (totalItems === 0 && event.key !== 'Tab') {
      return;
    }

    if (document.activeElement === this.refs.viewAllButton && (event.key === 'ArrowDown' || event.key === 'ArrowUp')) {
      return;
    }

    const currentIndex = this.#currentIndex;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab': {
        const viewAll = this.refs.viewAllButton;
        const drawerClose = this.#getModalDrawerCloseButton();
        const drawerCloseFocusable = drawerClose !== null && this.#isFocusableForModalTab(drawerClose);

        if (drawerCloseFocusable && document.activeElement === drawerClose) {
          if (!event.shiftKey) {
            event.preventDefault();
            this.#clearResultsListSelection();
            this.refs.searchInput.focus();
            return;
          }
          event.preventDefault();
          if (viewAll && this.#isViewAllButtonFocusable(viewAll)) {
            viewAll.focus();
          } else if (totalItems > 0) {
            this.#isKeyboardNavigation = true;
            this.#currentIndex = totalItems - 1;
          } else {
            this.refs.searchInput.focus();
          }
          return;
        }

        if (document.activeElement === viewAll && !event.shiftKey) {
          event.preventDefault();
          this.#clearResultsListSelection();
          if (drawerCloseFocusable) {
            drawerClose.focus();
          } else {
            this.refs.searchInput.focus();
          }
          return;
        }

        if (event.shiftKey && document.activeElement === viewAll) {
          event.preventDefault();
          if (totalItems === 0) {
            this.#clearResultsListSelection();
            this.refs.searchInput.focus();
          } else {
            this.#isKeyboardNavigation = true;
            this.#currentIndex = totalItems - 1;
          }
          return;
        }
        if (
          event.shiftKey &&
          document.activeElement === this.refs.searchInput &&
          drawerCloseFocusable &&
          this.#currentIndex < 0
        ) {
          event.preventDefault();
          this.#clearResultsListSelection();
          drawerClose.focus();
          return;
        }
        if (
          !event.shiftKey &&
          totalItems > 0 &&
          currentIndex === totalItems - 1 &&
          viewAll instanceof HTMLButtonElement &&
          this.#isViewAllButtonFocusable(viewAll)
        ) {
          event.preventDefault();
          this.#clearResultsListSelection();
          viewAll.focus();
          return;
        }
        if (totalItems === 0) {
          break;
        }
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;
      }

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
      this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
    });
  }

  /** @param {string} searchTerm */
  #syncSearchFooterTerms(searchTerm) {
    const el = this.refs.searchFooterTerms;
    if (!(el instanceof HTMLElement)) return;

    const template = Theme.translations?.search_results_for_terms_template;
    if (!searchTerm || typeof template !== 'string') {
      el.textContent = '';
      el.hidden = true;
      return;
    }

    el.textContent = template.split('__SEARCH_TERMS_PLACEHOLDER__').join(searchTerm);
    el.hidden = false;
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');
    url.searchParams.set('resources[limit]', '4');
    url.searchParams.set('resources[type]', 'product,query');
    url.searchParams.set('resources[options][fields]', PREDICTIVE_SEARCH_PRODUCT_FIELDS);

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        const parsedDoc = new DOMParser().parseFromString(resultsMarkup, 'text/html');
        const parsedResults = parsedDoc.getElementById(PREDICTIVE_SEARCH_LEGACY_RESULTS_ID);

        if (parsedResults) {
          morph(predictiveSearchResults, parsedResults);
        } else {
          morph(predictiveSearchResults, resultsMarkup);
        }

        this.#rewritePredictiveIdsInRoot(predictiveSearchResults);

        this.#resetScrollPositions();
        this.#syncSearchFooterTerms(searchTerm);
        // Header search dropdown: re-run clip + results max-height on `search-menu` (see `search-menu.js`).
        this.closest('search-menu')?.dispatchEvent(new CustomEvent('theme:submenu-layout'));
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.#recentlyViewedInPredictiveSearchEnabled) return null;
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:£{id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.#hideResetButton();
    this.#syncSearchFooterTerms('');

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    this.#rewritePredictiveIdsInRoot(parsedEmptySectionMarkup);

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (this.#recentlyViewedInPredictiveSearchEnabled && viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (recentlyViewedMarkup) {
        const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
        this.#rewritePredictiveIdsInRoot(parsedRecentlyViewedMarkup);
        const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById(this.#productsContainerId);
        if (recentlyViewedProductsHtml) {
          for (const child of recentlyViewedProductsHtml.children) {
            if (child instanceof HTMLElement) {
              child.setAttribute('ref', 'recentlyViewedWrapper');
            }
          }

          const collectionElement = parsedEmptySectionMarkup.querySelector(`#£{CSS.escape(this.#productsContainerId)}`);
          collectionElement?.prepend(...recentlyViewedProductsHtml.children);
        }
      }
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
    this.#syncSearchFooterTerms('');
    // Empty state / reset: footer and subtree height change in the header dropdown.
    this.closest('search-menu')?.dispatchEvent(new CustomEvent('theme:submenu-layout'));
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
